import { BookOpen, X } from 'lucide-react';

import { Button } from '@/components/ui/button';

interface SyntaxGuidePanelProps {
  open: boolean;
  onClose: () => void;
}

export const SyntaxGuidePanel = ({ open, onClose }: SyntaxGuidePanelProps) => {
  if (!open) return null;

  return (
    <div className="bg-muted/30 w-80 shrink-0 overflow-y-auto border-l p-4">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4" />
          <h3 className="text-sm font-semibold">Template Syntax Guide</h3>
        </div>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose}>
          <X className="h-3 w-3" />
        </Button>
      </div>

      <div className="space-y-4 text-xs">
        <section>
          <h4 className="mb-1 font-semibold text-blue-600">Variables</h4>
          <pre className="bg-muted rounded p-2 text-[11px]">
            {`{{review.lead_data.customer_name}}\n{{contact.phone}}\n{{call.outcome}}`}
          </pre>
        </section>

        <section>
          <h4 className="mb-1 font-semibold text-orange-600">Fallbacks</h4>
          <pre className="bg-muted rounded p-2 text-[11px]">
            {`{{contact.email || "no email"}}\n{{contact.name || contact.phone}}`}
          </pre>
        </section>

        <section>
          <h4 className="mb-1 font-semibold text-orange-600">Ternary</h4>
          <pre className="bg-muted rounded p-2 text-[11px]">
            {`{{var ? "yes" : "no"}}\n{{var == true ? "Yes" : "No"}}\n{{var == "LEAD" ? "Lead!" : "Other"}}\n{{var != "VM" ? "Not VM" : "Is VM"}}`}
          </pre>
          <p className="text-muted-foreground mt-1">Branch values must be quoted.</p>
        </section>

        <section>
          <h4 className="mb-1 font-semibold text-purple-600">Conditionals</h4>
          <pre className="bg-muted rounded p-2 text-[11px]">
            {`{{#if contact.name}}\n  Hello {{contact.name}}\n{{#else}}\n  Hello stranger\n{{/if}}`}
          </pre>
          <p className="text-muted-foreground mt-1 mb-2">Supports comparisons:</p>
          <pre className="bg-muted rounded p-2 text-[11px]">
            {`{{#if var == "LEAD_CAPTURED"}}\n  Lead captured!\n{{#else}}\n  Other outcome\n{{/if}}`}
          </pre>
          <p className="text-muted-foreground mt-1">
            Use <code className="text-purple-500">{'{{#if}}'}</code> for multi-line HTML. Use
            ternary for inline text only.
          </p>
        </section>

        <section>
          <h4 className="text-muted-foreground mb-1 font-semibold">Available Paths</h4>
          <div className="bg-muted space-y-1 rounded p-2 text-[11px]">
            <div>
              <span className="font-semibold">call.*</span> — id, outcome, duration, phone_number,
              lead_data.*
            </div>
            <div>
              <span className="font-semibold">contact.*</span> — name, phone
            </div>
            <div>
              <span className="font-semibold">campaign.*</span> — name, id
            </div>
            <div>
              <span className="font-semibold">config.*</span> — notification_emails,
              high_intent_emails
            </div>
            <div>
              <span className="font-semibold">review.*</span> — reviewed_outcome, lead_data.*,
              additional_review_data.*, ai_analysis
            </div>
            <div>
              <span className="font-semibold">{'nodes.<id>.*'}</span> — status, sms_sent, branch
            </div>
            <div>
              <span className="font-semibold">sms_sent</span> — global SMS flag
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
